import React, { useMemo } from "react";

import RectangleNode from "./shapes/RectangleNode";
import EllipseNode from "./shapes/EllipseNode";
import HexagonNode from "./shapes/HexagonNode";
import ParallelogramNode from "./shapes/ParallelogramNode";
import RhombusNode from "./shapes/RhombusNode";

// --- Shape Constants ---
const SHAPES = {
  RECTANGLE: "rectangle",
  ELLIPSE: "ellipse",
  HEXAGON: "hexagon",
  PARALLELOGRAM: "parallelogram",
  RHOMBUS: "rhombus",
};

function normalize(val) {
  if (val == null) return null;
  let normalized = String(val).trim().toUpperCase();

  // Handle common variations
  if (normalized.includes('_')) {
    normalized = normalized.replace(/_/g, ' ');
  }
  if (normalized.includes('-')) {
    normalized = normalized.replace(/-/g, ' ');
  }

  return normalized;
}


// Subtitle mapping (from your nodes)
const SUBTITLE_TO_SHAPE = {
  // ===== ELLIPSE SUBTITLES (for PHASE) =====
  PHASE: SHAPES.ELLIPSE,
  "DESIGN PHASE": SHAPES.ELLIPSE,
  "DEVELOPMENT PHASE": SHAPES.ELLIPSE,
  "TESTING PHASE": SHAPES.ELLIPSE,
  "IMPLEMENTATION PHASE": SHAPES.ELLIPSE,
  "PLANNING PHASE": SHAPES.ELLIPSE,
  STAGE: SHAPES.ELLIPSE,
  GROUP: SHAPES.RECTANGLE,
  PROJECT: SHAPES.RECTANGLE,
  MODULE: SHAPES.RECTANGLE,

  // ===== ELLIPSE SUBTITLES =====
  START: SHAPES.ELLIPSE,
  "PROJECT START": SHAPES.ELLIPSE,
  END: SHAPES.ELLIPSE,
  "PROJECT END": SHAPES.ELLIPSE,
  FINISH: SHAPES.ELLIPSE,
  "PROJECT FINISH": SHAPES.ELLIPSE,
  COMPLETED: SHAPES.ELLIPSE,
  "MILESTONE COMPLETED": SHAPES.ELLIPSE,
  "TASK COMPLETED": SHAPES.ELLIPSE,

  // ===== HEXAGON SUBTITLES =====
  DELIVERABLE: SHAPES.HEXAGON,
  OUTPUT: SHAPES.HEXAGON,
  PRODUCT: SHAPES.HEXAGON,
  REVIEW: SHAPES.HEXAGON,
  "CODE REVIEW": SHAPES.HEXAGON,
  "DESIGN REVIEW": SHAPES.HEXAGON,
  "TEST REVIEW": SHAPES.HEXAGON,
  APPROVAL: SHAPES.HEXAGON,
  "CLIENT APPROVAL": SHAPES.HEXAGON,
  "MANAGER APPROVAL": SHAPES.HEXAGON,
  VALIDATION: SHAPES.HEXAGON,
  AUDIT: SHAPES.HEXAGON,
  QUALITY: SHAPES.HEXAGON,

  // ===== RECTANGLE SUBTITLES (for TASK) =====
  TASK: SHAPES.RECTANGLE,
  TASKS: SHAPES.RECTANGLE,
  "MAIN TASK": SHAPES.RECTANGLE,
  "SUB TASK": SHAPES.RECTANGLE,
  "SUB-TASK": SHAPES.RECTANGLE,
  ACTIVITY: SHAPES.RECTANGLE,
  "MAIN ACTIVITY": SHAPES.RECTANGLE,
  ACTION: SHAPES.RECTANGLE,
  "NEXT ACTION": SHAPES.RECTANGLE,
  OPERATION: SHAPES.RECTANGLE,
  PROCESS: SHAPES.RECTANGLE,
  STEP: SHAPES.RECTANGLE,
  "NEXT STEP": SHAPES.RECTANGLE,

  // ===== HEXAGON SUBTITLES (including MILESTONE/DECISION) =====
  MILESTONE: SHAPES.HEXAGON,
  "KEY MILESTONE": SHAPES.HEXAGON,
  "MAJOR MILESTONE": SHAPES.HEXAGON,
  DECISION: SHAPES.HEXAGON,
  "KEY DECISION": SHAPES.HEXAGON,
  "MAJOR DECISION": SHAPES.HEXAGON,
  "ARCHITECTURE DECISION": SHAPES.HEXAGON,
  "TECH STACK DECISION": SHAPES.HEXAGON,
  GATE: SHAPES.HEXAGON,
  "QUALITY GATE": SHAPES.HEXAGON,
  CHECKPOINT: SHAPES.HEXAGON,

  // ===== STATUS-RELATED =====
  DELAYED: SHAPES.HEXAGON,
  "AT RISK": SHAPES.HEXAGON,
  BLOCKED: SHAPES.HEXAGON,
  URGENT: SHAPES.HEXAGON,
  CRITICAL: SHAPES.HEXAGON,
  "ON HOLD": SHAPES.HEXAGON,

  "IN PROGRESS": SHAPES.PARALLELOGRAM,
  "IN WORK": SHAPES.PARALLELOGRAM,

  "TO DO": SHAPES.RECTANGLE,
  PENDING: SHAPES.RECTANGLE,
  "NOT STARTED": SHAPES.RECTANGLE,
};

const TYPE_TO_SHAPE = {
  // ===== ELLIPSE SHAPES (for PHASE) =====
  PHASE: SHAPES.ELLIPSE,
  PHASES: SHAPES.ELLIPSE,
  STAGE: SHAPES.ELLIPSE,
  STAGES: SHAPES.ELLIPSE,

  // ===== RECTANGLE SHAPES (for TASK) =====
  TASK: SHAPES.RECTANGLE,
  TASKS: SHAPES.RECTANGLE,
  ACTIVITY: SHAPES.RECTANGLE,
  ACTIVITIES: SHAPES.RECTANGLE,
  SUBTASK: SHAPES.RECTANGLE,
  SUB_TASK: SHAPES.RECTANGLE,
  ACTION: SHAPES.RECTANGLE,
  ACTIONS: SHAPES.RECTANGLE,
  ACTION_ITEM: SHAPES.RECTANGLE,
  WORK_ITEM: SHAPES.RECTANGLE,
  TODO: SHAPES.RECTANGLE,
  TO_DO: SHAPES.RECTANGLE,
  OPERATION: SHAPES.RECTANGLE,
  PROCESS: SHAPES.RECTANGLE,
  STEP: SHAPES.RECTANGLE,

  // ===== OTHER RECTANGLE SHAPES =====
  GROUP: SHAPES.RECTANGLE,
  GROUPS: SHAPES.RECTANGLE,
  PROJECT: SHAPES.RECTANGLE,
  PROJECTS: SHAPES.RECTANGLE,
  MODULE: SHAPES.RECTANGLE,
  MODULES: SHAPES.RECTANGLE,
  EPIC: SHAPES.RECTANGLE,
  EPICS: SHAPES.RECTANGLE,
  CATEGORY: SHAPES.RECTANGLE,

  // ===== ELLIPSE SHAPES =====
  START: SHAPES.ELLIPSE,
  BEGIN: SHAPES.ELLIPSE,
  INITIAL: SHAPES.ELLIPSE,
  END: SHAPES.ELLIPSE,
  FINISH: SHAPES.ELLIPSE,
  FINAL: SHAPES.ELLIPSE,
  TERMINAL: SHAPES.ELLIPSE,
  COMPLETED: SHAPES.ELLIPSE,
  DONE: SHAPES.ELLIPSE,
  FINISHED: SHAPES.ELLIPSE,

  // ===== HEXAGON SHAPES =====
  DELIVERABLE: SHAPES.HEXAGON,
  DELIVERABLES: SHAPES.HEXAGON,
  OUTPUT: SHAPES.HEXAGON,
  OUTPUTS: SHAPES.HEXAGON,
  RESULT: SHAPES.HEXAGON,
  PRODUCT: SHAPES.HEXAGON,
  ARTIFACT: SHAPES.HEXAGON,
  REVIEW: SHAPES.HEXAGON,
  REVIEWS: SHAPES.HEXAGON,
  APPROVAL: SHAPES.HEXAGON,
  APPROVALS: SHAPES.HEXAGON,
  AUDIT: SHAPES.HEXAGON,
  VALIDATION: SHAPES.HEXAGON,
  QUALITY: SHAPES.HEXAGON,

  // ===== HEXAGON SHAPES (including MILESTONE/DECISION) =====
  MILESTONE: SHAPES.HEXAGON,
  MILESTONES: SHAPES.HEXAGON,
  DECISION: SHAPES.HEXAGON,
  DECISIONS: SHAPES.HEXAGON,
  GATE: SHAPES.HEXAGON,
  GATES: SHAPES.HEXAGON,
  CHECKPOINT: SHAPES.HEXAGON,
  CHECKPOINTS: SHAPES.HEXAGON,
  KEY_MILESTONE: SHAPES.HEXAGON,
  KEY_DECISION: SHAPES.HEXAGON,
  ARCHITECTURE_DECISION: SHAPES.HEXAGON,

  // ===== RISK/ISSUE SHAPES =====
  RISK: SHAPES.HEXAGON,
  RISKS: SHAPES.HEXAGON,
  ISSUE: SHAPES.HEXAGON,
  ISSUES: SHAPES.HEXAGON,
  PROBLEM: SHAPES.HEXAGON,
  PROBLEMS: SHAPES.HEXAGON,
  CHALLENGE: SHAPES.HEXAGON,
  CHALLENGES: SHAPES.HEXAGON,
  BLOCKER: SHAPES.HEXAGON,
};

// Helper to extract subtitle
function getSubtitle(node) {
  return node.subtitle || node.subTitle || node.sub_type_title || "";
}

// Main shape resolver
function resolveShapeFromNode(node) {
  // Priority 1: Explicit shape property
  if (node.shape) {
    const shapeLower = String(node.shape).toLowerCase();
    if (Object.values(SHAPES).includes(shapeLower)) {
      return shapeLower;
    }
  }

  // Priority 2: Subtitle-based mapping
  const subtitle = normalize(getSubtitle(node));
  if (subtitle) {
    // Try exact match first
    if (SUBTITLE_TO_SHAPE[subtitle]) {
      return SUBTITLE_TO_SHAPE[subtitle];
    }

    // Enhanced partial matching
    if (subtitle.includes('MILESTONE') || subtitle.includes('DECISION') || subtitle.includes('GATE') || subtitle.includes('CHECKPOINT')) {
      return SHAPES.HEXAGON;
    }
    // TASK now uses RECTANGLE
    if (subtitle.includes('TASK') || subtitle.includes('ACTIVITY') || subtitle.includes('ACTION') || subtitle.includes('STEP') || subtitle.includes('OPERATION') || subtitle.includes('PROCESS')) {
      return SHAPES.RECTANGLE;
    }
    // PHASE now uses PARALLELOGRAM
    if (subtitle.includes('PHASE') || subtitle.includes('STAGE')) {
      return SHAPES.PARALLELOGRAM;
    }
    if (subtitle.includes('GROUP') || subtitle.includes('MODULE') || subtitle.includes('EPIC') || subtitle.includes('PROJECT')) {
      return SHAPES.RECTANGLE;
    }
    if (subtitle.includes('DELIVERABLE') || subtitle.includes('OUTPUT') || subtitle.includes('PRODUCT') || subtitle.includes('RESULT') || subtitle.includes('ARTIFACT')) {
      return SHAPES.HEXAGON;
    }
    if (subtitle.includes('REVIEW') || subtitle.includes('APPROVAL') || subtitle.includes('AUDIT') || subtitle.includes('VALIDATION') || subtitle.includes('QUALITY')) {
      return SHAPES.HEXAGON;
    }
    if (subtitle.includes('START') || subtitle.includes('END') || subtitle.includes('FINISH') || subtitle.includes('BEGIN') || subtitle.includes('INITIAL') || subtitle.includes('FINAL')) {
      return SHAPES.ELLIPSE;
    }
    if (subtitle.includes('COMPLETED') || subtitle.includes('DONE') || subtitle.includes('FINISHED')) {
      return SHAPES.ELLIPSE;
    }
    if (subtitle.includes('RISK') || subtitle.includes('ISSUE') || subtitle.includes('PROBLEM') || subtitle.includes('CHALLENGE') || subtitle.includes('BLOCKER')) {
      return SHAPES.HEXAGON;
    }
    if (subtitle.includes('DELAYED') || subtitle.includes('LATE') || subtitle.includes('AT RISK') || subtitle.includes('BLOCKED') || subtitle.includes('URGENT') || subtitle.includes('CRITICAL')) {
      return SHAPES.HEXAGON;
    }
    if (subtitle.includes('IN PROGRESS') || subtitle.includes('IN WORK') || subtitle.includes('PROGRESS') || subtitle.includes('WORKING')) {
      return SHAPES.PARALLELOGRAM;
    }
    if (subtitle.includes('TO DO') || subtitle.includes('PENDING') || subtitle.includes('NOT STARTED')) {
      return SHAPES.RECTANGLE;
    }
  }

  // Priority 3: Type-based mapping
  const type = normalize(node.type);
  if (type && TYPE_TO_SHAPE[type]) {
    return TYPE_TO_SHAPE[type];
  }

  // Priority 4: SubType mapping
  const subType = normalize(node.subType || node.subtitle_type || node.category);
  if (subType && TYPE_TO_SHAPE[subType]) {
    return TYPE_TO_SHAPE[subType];
  }

  // Priority 5: Check in title/description
  const title = normalize(node.title || node.name);
  const description = normalize(node.description);

  if (title?.includes('MILESTONE') || description?.includes('MILESTONE')) {
    return SHAPES.HEXAGON;
  }
  // TASK now uses RECTANGLE
  if (title?.includes('TASK') || description?.includes('TASK')) {
    return SHAPES.RECTANGLE;
  }
  // PHASE now uses PARALLELOGRAM
  if (title?.includes('PHASE') || description?.includes('PHASE')) {
    return SHAPES.PARALLELOGRAM;
  }
  if (title?.includes('DELIVERABLE') || description?.includes('DELIVERABLE')) {
    return SHAPES.HEXAGON;
  }
  if (title?.includes('START') || description?.includes('START')) {
    return SHAPES.ELLIPSE;
  }

  // Default: Rectangle
  return SHAPES.RECTANGLE;
}


// --- Dynamic Color Generation ---
// Uses the Golden Ratio (φ) to generate highly distinct hues for any number of levels
function getDynamicColor(level) {
  const goldenRatioConjugate = 0.618033988749895;
  const h = (level * goldenRatioConjugate) % 1;
  const hue = h * 360;

  // Premium Pastel Range
  return `hsl(${hue}, 70%, 94%)`;
}

// Get node color based on hierarchy depth, isInitial flag, and isChild status
function getNodeColor(node, isInitial, isChild) {
  // 1. Root Nodes (not a child) - Use Type-based coloration
  if (!isChild) {
    const type = (node.subType || node.type || "").toLowerCase();
    if (type.includes('activity')) {
      return '#E3F2FD'; // Light blue
    } else if (type.includes('milestone')) {
      return '#F3E5F5'; // Light purple
    } else if (type.includes('phase')) {
      return '#E8F5E9'; // Light green
    }
    return '#FFFFFF'; // Default root color
  }

  // 2. Hierarchy Nodes (expanded children) - Use Dynamic Level-based coloration
  const depth = node.hierarchyDepth || node.level || 0;
  return getDynamicColor(depth);
}

// ========== NODE RENDERER COMPONENT ==========
const NodeRenderer = ({
  node,
  onMouseDown,
  isInitial,
  onDoubleClick,
  isChild,
  isInChain,
  opacity = 1,
  showCriticalPath,
  onHighlightChain,
  dragNode,
  loading,
  filter,
  isExpanded,
  hasChildren,
  onRefresh,
  onUpdateNode,
  onPromoteNode,
  // Additional props
  forceResetHover,
  projectId,
  zoomRatio,
  timelineMode,
  ...rest
}) => {
  // Determine shape
  const shape = useMemo(() => resolveShapeFromNode(node), [node]);

  // Get node color based on isInitial flag and isChild status
  const nodeColor = useMemo(() => getNodeColor(node, isInitial, isChild), [node, isInitial, isChild]);

  // --- Calculate Delay Days ---
  const delayDays = useMemo(() => {
    const rawDue = node.rawDueDate;
    const rawActualFinish = node.rawActualFinishDate;
    const maturity = (node.maturity || "").toLowerCase();

    if (!rawDue) return null;

    // Helper to parse date-only (ignores time/timezone shifts)
    const parseDateOnly = (dateStr) => {
      if (!dateStr) return null;
      const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
      if (match) {
        const [y, m, d] = match[1].split("-").map(Number);
        return new Date(y, m - 1, d); // Local midnight
      }
      const d = new Date(dateStr);
      d.setHours(0, 0, 0, 0);
      return d;
    };

    const dueDate = parseDateOnly(rawDue);
    if (!dueDate) return null;

    // CASE 1: Task is COMPLETED or RELEASED
    if (maturity.includes("complete") || maturity.includes("done") || maturity.includes("release")) {
      if (!rawActualFinish) return null;

      const actualFinishDate = parseDateOnly(rawActualFinish);

      if (actualFinishDate > dueDate) {
        const diffTime = actualFinishDate - dueDate;
        return Math.round(diffTime / (1000 * 60 * 60 * 24));
      }
      return null;
    }

    // CASE 2: Task is ACTIVE (In Work, etc.)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (today > dueDate) {
      const diffTime = today - dueDate;
      return Math.round(diffTime / (1000 * 60 * 60 * 24));
    }
    return null;
  }, [node.rawDueDate, node.rawActualFinishDate, node.maturity]);

  // Get subtitle
  const subtitle = getSubtitle(node);

  // Enhanced node properties for shape components with shape-specific sizes
  const shapeProps = useMemo(() => {
    // Get base width and height from node or use shape-specific defaults
    let nodeWidth = node.width;
    let nodeHeight = node.height;

    // If no width/height specified in node data, use shape-specific defaults
    if (nodeWidth === undefined && nodeHeight === undefined) {
      // Set shape-specific default sizes
      switch (shape) {
        case SHAPES.RHOMBUS:
          nodeWidth = 180;  // Larger than rectangle: 160 -> 200 (+25%)
          nodeHeight = 85;  // Larger than rectangle: 65 -> 85 (+31%)
          break;
        case SHAPES.RECTANGLE:
          nodeWidth = 160;
          nodeHeight = 60;
          break;
        case SHAPES.ELLIPSE:
          nodeWidth = 220;
          nodeHeight = 90;
          break;
        case SHAPES.HEXAGON:
          nodeWidth = 160;
          nodeHeight = 55;
          break;
        case SHAPES.PARALLELOGRAM:
          nodeWidth = 180;
          nodeHeight = 65;
          break;
        default:
          nodeWidth = 300;
          nodeHeight = 200;
      }
    }

    return {
      id: node.id,
      x: node.x || 0,
      y: node.y || 0,
      width: nodeWidth,  // CRITICAL: Use the shape-specific width
      height: nodeHeight,
      title: node.title || node.name || node.displayName || `Node ${node.id}`,
      subtitle: subtitle,
      isChild: isChild,
      startDate: node.startDate || "",
      dueDate: node.dueDate || node.endDate || node.date || "",
      assignees: Array.isArray(node.assignees)
        ? node.assignees
        : (node.assignee || node.assignedTo || node.responsible)
          ? [node.assignee || node.assignedTo || node.responsible]
          : [],
      maturity: node.maturity || node.status || "",
      maturityDescription: node.maturityDescription || node.statusDescription || "",
      status: node.status || "",
      statusDescription: node.statusDescription || "",
      description: node.description || node.desc || "",
      dragNode: dragNode,
      hasChildren: hasChildren || node.hasChildren || false,
      isExpanded: isExpanded || false,
      onDoubleClick: () => onDoubleClick && onDoubleClick(node.id),
      onMouseDown: (e) => onMouseDown && onMouseDown(e, node.id),
      hierarchyDepth: node.hierarchyDepth || node.level || 0,
      objectId: node.objectId || node.id,
      totalFloat: node.totalFloat || 0,
      showCriticalPath: showCriticalPath || false,
      isInChain: isInChain || false,
      onHighlightChain: onHighlightChain,
      opacity: opacity,
      loading: loading || false,
      onRefresh: onRefresh,
      onUpdateNode: onUpdateNode,
      onPromoteNode: onPromoteNode,
      projectId: projectId,
      forceResetHover: forceResetHover || false,
      nodeColor: nodeColor, // Pass the computed node color
      delayDays: delayDays, // Pass calculated delay
      isInitial: isInitial, // Pass the isInitial flag
      zoomRatio: zoomRatio, // Pass zoomRatio for DeliverablesDots guard
      timelineMode: timelineMode || false,
      ...rest // Pass any other props
    };
  }, [
    node,
    dragNode,
    hasChildren,
    isChild,
    isExpanded,
    onDoubleClick,
    onMouseDown,
    onHighlightChain,
    isInChain,
    showCriticalPath,
    opacity,
    loading,
    forceResetHover,
    projectId,
    onRefresh,
    onUpdateNode,
    onPromoteNode,
    subtitle,
    nodeColor,
    isInitial,
    delayDays,
    shape,
    zoomRatio,
    timelineMode,
    rest
  ]);


  // Render the appropriate shape
  const renderShape = () => {
    switch (shape) {
      case SHAPES.ELLIPSE:
        return <EllipseNode {...shapeProps} />;
      case SHAPES.HEXAGON:
        return <HexagonNode {...shapeProps} />;
      case SHAPES.PARALLELOGRAM:
        return <ParallelogramNode {...shapeProps} />;
      case SHAPES.RHOMBUS:
        return <RhombusNode {...shapeProps} />;
      case SHAPES.RECTANGLE:
      default:
        return <RectangleNode {...shapeProps} />;
    }
  };

  return (
    <g
      data-node-id={node.id}
      data-shape={shape}
      data-type={node.type}
      data-status={node.status}
      data-is-initial={isInitial ? "true" : "false"}
      transform={`translate(${dragNode === node.id ? 2 : 0}, ${dragNode === node.id ? 2 : 0})`}
      style={{
        cursor: dragNode ? 'grabbing' : 'grab',
        opacity,
        userSelect: 'none',
        pointerEvents: 'auto',
      }}
      filter={filter}
    >
      {/* Render the shape */}
      {renderShape()}
    </g>
  );
};

// Memoization comparison function with shape comparison
const areEqual = (prevProps, nextProps) => {
  const p = prevProps.node;
  const n = nextProps.node;

  // Resolve shapes for comparison
  const prevShape = resolveShapeFromNode(p);
  const nextShape = resolveShapeFromNode(n);

  const basicEqual =
    p.id === n.id &&
    p.x === n.x &&
    p.y === n.y &&
    p.width === n.width &&
    p.height === n.height &&
    p.shape === n.shape &&
    p.status === n.status &&
    p.subtitle === n.subtitle &&
    p.type === n.type &&
    p.assignees === n.assignees &&
    p.title === n.title &&
    p.maturity === n.maturity && // Add maturity to comparison
    p.startDate === n.startDate &&
    p.dueDate === n.dueDate &&
    p.rawDueDate === n.rawDueDate &&
    p.rawActualFinishDate === n.rawActualFinishDate &&
    prevShape === nextShape && // Compare resolved shapes for proper memoization
    prevProps.isInChain === nextProps.isInChain &&
    prevProps.opacity === nextProps.opacity &&
    prevProps.isExpanded === nextProps.isExpanded &&
    prevProps.hasChildren === nextProps.hasChildren &&
    prevProps.dragNode === nextProps.dragNode &&
    prevProps.showCriticalPath === nextProps.showCriticalPath &&
    prevProps.forceResetHover === nextProps.forceResetHover &&
    prevProps.isInitial === nextProps.isInitial &&
    prevProps.zoomRatio === nextProps.zoomRatio; // Add zoomRatio to comparison

  return basicEqual;
};

// Memoized version for performance
export const MemoizedNodeRenderer = React.memo(NodeRenderer, areEqual);

export default NodeRenderer;